import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {api} from "../../app/api";

const postMultipart = (url, data) => api.post(url, data, {
    timeout: 180000,
    transformRequest: [(body, headers) => {
        if (typeof FormData !== 'undefined' && body instanceof FormData && headers) {
            delete headers['Content-Type'];
            delete headers['content-type'];
        }
        return body;
    }],
});


export const fetchApplications = createAsyncThunk(
    'declaration/fetchApplications',
    async () => {
        api.defaults.headers.common = {
            ...api.defaults.headers.common,
            Authorization: `Bearer ${localStorage.getItem('token')}`
        }
        const response = await api.get('/applications')
        return response.data
    }
)
export const fetchApplicationTypes = createAsyncThunk(
    'declaration/types',
    async () => {
        api.defaults.headers.common = {
            ...api.defaults.headers.common,
            Authorization: `Bearer ${localStorage.getItem('token')}`
        }
        const response = await api.get('/applications/application-types')
        const raw = response?.data
        // Normalize potential API shapes to an array
        if (Array.isArray(raw)) return raw
        if (Array.isArray(raw?.data)) return raw.data
        if (Array.isArray(raw?.items)) return raw.items
        if (Array.isArray(raw?.application_types)) return raw.application_types
        if (Array.isArray(raw?.types)) return raw.types
        if (Array.isArray(raw?.result)) return raw.result
        if (Array.isArray(raw?.payload)) return raw.payload
        if (Array.isArray(raw?.data?.items)) return raw.data.items
        if (Array.isArray(raw?.data?.application_types)) return raw.data.application_types
        return []
    }
)
export const fetchFields = createAsyncThunk(
    'declaration/fields',
    async ({id}) => {
        api.defaults.headers.common = {
            ...api.defaults.headers.common,
            Authorization: `Bearer ${localStorage.getItem('token')}`
        }
        const response = await api.get(`applications/application-types/${id}`)
        return response.data
    }
)

export const sendFields = createAsyncThunk(
    'declaration/sendFields',
    async (data, { rejectWithValue }) => {
        try {
            api.defaults.headers.common = {
                ...api.defaults.headers.common,
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
            const response = await postMultipart(`/applications`, data)
            return response.data
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Произошла ошибка при отправке заявления',
                status: error.response?.status
            })
        }
    }
)

// Update existing application (for на доработку)
// Backend: POST /applications/{application} -> updateApplication (route added in api.php / api-v2.php).
export const updateApplication = createAsyncThunk(
    'declaration/updateApplication',
    async ({ applicationId, data }, { rejectWithValue }) => {
        try {
            api.defaults.headers.common = {
                ...api.defaults.headers.common,
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
            const response = await postMultipart(`/applications/${applicationId}`, data)
            return response.data
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Произошла ошибка при обновлении заявления',
                status: error.response?.status
            })
        }
    }
)

// Copy Application Feature
export const fetchApplicationById = createAsyncThunk(
    'declaration/fetchApplicationById',
    async (applicationId, { rejectWithValue }) => {
        try {
            api.defaults.headers.common = {
                ...api.defaults.headers.common,
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
            const response = await api.get(`/applications/${applicationId}`)
            return response.data
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Ошибка при получении заявления',
                status: error.response?.status
            })
        }
    }
)

// Draft Management
export const saveDraft = createAsyncThunk(
    'declaration/saveDraft',
    async (draftData, { rejectWithValue }) => {
        try {
            api.defaults.headers.common = {
                ...api.defaults.headers.common,
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
            const response = await api.post('/applications/drafts', draftData)
            return response.data
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Ошибка при сохранении черновика',
                status: error.response?.status
            })
        }
    }
)

export const updateDraft = createAsyncThunk(
    'declaration/updateDraft',
    async ({ draftId, draftData }, { rejectWithValue }) => {
        try {
            api.defaults.headers.common = {
                ...api.defaults.headers.common,
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
            const response = await api.put(`/applications/drafts/${draftId}`, draftData)
            return response.data
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Ошибка при обновлении черновика',
                status: error.response?.status
            })
        }
    }
)

// Normalize API response to always return an array of drafts
function normalizeDraftsPayload(raw) {
    if (Array.isArray(raw)) return raw
    if (Array.isArray(raw?.data)) return raw.data
    if (Array.isArray(raw?.drafts)) return raw.drafts
    if (Array.isArray(raw?.items)) return raw.items
    if (Array.isArray(raw?.result)) return raw.result
    return []
}

export const fetchDrafts = createAsyncThunk(
    'declaration/fetchDrafts',
    async (_, { rejectWithValue }) => {
        try {
            api.defaults.headers.common = {
                ...api.defaults.headers.common,
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
            const response = await api.get('/applications/drafts')
            return normalizeDraftsPayload(response.data)
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Ошибка при получении черновиков',
                status: error.response?.status
            })
        }
    }
)

export const fetchDraftById = createAsyncThunk(
    'declaration/fetchDraftById',
    async (draftId, { rejectWithValue }) => {
        try {
            api.defaults.headers.common = {
                ...api.defaults.headers.common,
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
            const response = await api.get(`/applications/drafts/${draftId}`)
            return response.data
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Ошибка при получении черновика',
                status: error.response?.status
            })
        }
    }
)

const declarationSlice = createSlice({
    name: 'declaration',
    initialState: {
        // loading: false,
        fields: null,
        applications: null,
        applicationTypes: null,
        applicationTypeId: null,
        fieldOptions:[],
        declarationNumber: '',
        fulledFields: {},
        copiedApplication: null,
        drafts: [],
        currentDraft: null,
        draftId: null,
        lastSavedAt: null,
        isSavingDraft: false,
        error: {
            failed: false,
            message: null,
        },
    },
    reducers: {
        setFulledFields: (state, action) => {
            // Обновляем значения полей в хранилище
            state.fulledFields = { ...state.fulledFields, ...action.payload };
        },
    },
    extraReducers: {
        [fetchApplications.pending]: (state) => {
            state.loading = true
            state.error.failed = false;
            state.error.message = null;
        },
        [fetchApplications.fulfilled]: (state, action) => {
            state.loading = false
            state.applications = action.payload
        },
        [fetchApplications.rejected]: (state, action) => {
            state.loading = false
            state.error.failed = true;
            state.error.message = action.payload;
        },
        [fetchFields.pending]: (state) => {
            state.loading = true
            state.error.failed = false;
            state.error.message = null;
        },
        [fetchFields.fulfilled]: (state, action) => {
            state.loading = false
            const payload = action.payload

            // Normalize so declaration form and review always have fields.applicationType,
            // but keep backward compatibility with existing shape.
            //
            // Possible payload shapes:
            // 1) { applicationType: { ... } }          // current frontend expectation
            // 2) { application_type: { ... } }         // backend-style snake_case
            // 3) { id, name, active_field_blocks, ...} // raw application type object
            if (payload?.applicationType) {
                // Already in expected shape – keep as is
                state.fields = payload
            } else if (payload?.application_type) {
                // Wrap snake_case into expected camelCase container
                state.fields = { applicationType: payload.application_type }
            } else {
                // Treat payload itself as the application type object
                state.fields = { applicationType: payload }
            }
        },
        [fetchFields.rejected]: (state, action) => {
            state.loading = false
            state.error.failed = true;
            state.error.message = action.payload;
        },
        [fetchApplicationTypes.pending]: (state) => {
            state.loading = true
            state.error.failed = false;
            state.error.message = null;
        },
        [fetchApplicationTypes.fulfilled]: (state, action) => {
            state.loading = false
            state.applicationTypes = action.payload
        },
        [fetchApplicationTypes.rejected]: (state, action) => {
            state.loading = false
            state.error.failed = true;
            state.error.message = action.payload;
        },
        [sendFields.pending]: (state) => {
            state.loading = true
            state.error.failed = false;
            state.error.message = null;
        },
        [sendFields.fulfilled]: (state, action) => {
            state.loading = false
            console.log(action.payload.id)
            state.declarationNumber = action.payload.id
        },
        [sendFields.rejected]: (state, action) => {
            state.loading = false
            state.error.failed = true;
            state.error.message = action.payload?.message || 'Произошла ошибка при отправке заявления';
        },
        // Update Application
        [updateApplication.pending]: (state) => {
            state.loading = true
            state.error.failed = false;
            state.error.message = null;
        },
        [updateApplication.fulfilled]: (state, action) => {
            state.loading = false
            console.log(action.payload.id)
            state.declarationNumber = action.payload.id
        },
        [updateApplication.rejected]: (state, action) => {
            state.loading = false
            state.error.failed = true;
            state.error.message = action.payload?.message || 'Произошла ошибка при обновлении заявления';
        },
        // Copy Application
        [fetchApplicationById.pending]: (state) => {
            state.loading = true
            state.error.failed = false;
            state.error.message = null;
        },
        [fetchApplicationById.fulfilled]: (state, action) => {
            state.loading = false
            state.copiedApplication = action.payload
        },
        [fetchApplicationById.rejected]: (state, action) => {
            state.loading = false
            state.error.failed = true;
            state.error.message = action.payload?.message || 'Ошибка при получении заявления';
        },
        // Draft Management
        [saveDraft.pending]: (state) => {
            state.isSavingDraft = true
        },
        [saveDraft.fulfilled]: (state, action) => {
            state.isSavingDraft = false
            state.currentDraft = action.payload
            state.draftId = action.payload.id
            state.lastSavedAt = action.payload.updated_at || new Date().toISOString()
        },
        [saveDraft.rejected]: (state, action) => {
            state.isSavingDraft = false
            state.error.failed = true;
            state.error.message = action.payload?.message || 'Ошибка при сохранении черновика';
        },
        [updateDraft.pending]: (state) => {
            state.isSavingDraft = true
        },
        [updateDraft.fulfilled]: (state, action) => {
            state.isSavingDraft = false
            state.currentDraft = action.payload
            state.lastSavedAt = action.payload.updated_at || new Date().toISOString()
        },
        [updateDraft.rejected]: (state, action) => {
            state.isSavingDraft = false
            state.error.failed = true;
            state.error.message = action.payload?.message || 'Ошибка при обновлении черновика';
        },
        [fetchDrafts.pending]: (state) => {
            state.loading = true
        },
        [fetchDrafts.fulfilled]: (state, action) => {
            state.loading = false
            // payload is already normalized to array by fetchDrafts thunk
            state.drafts = Array.isArray(action.payload) ? action.payload : []
        },
        [fetchDrafts.rejected]: (state, action) => {
            state.loading = false
            state.error.failed = true;
            state.error.message = action.payload?.message || 'Ошибка при получении черновиков';
        },
        [fetchDraftById.pending]: (state) => {
            state.loading = true
        },
        [fetchDraftById.fulfilled]: (state, action) => {
            state.loading = false
            state.currentDraft = action.payload
            state.draftId = action.payload.id
            state.lastSavedAt = action.payload.updated_at || null
        },
        [fetchDraftById.rejected]: (state, action) => {
            state.loading = false
            state.error.failed = true;
            state.error.message = action.payload?.message || 'Ошибка при получении черновика';
        },
    },
    reducers: {
        setFulledFields: (state, action) => {
            // Обновляем значения полей в хранилище
            state.fulledFields = { ...state.fulledFields, ...action.payload };
        },
        clearCopiedApplication: (state) => {
            state.copiedApplication = null
        },
        clearDraft: (state) => {
            state.currentDraft = null
            state.draftId = null
            state.lastSavedAt = null
        },
        setDraftId: (state, action) => {
            state.draftId = action.payload
        },
    }
})

export const { setFulledFields, clearCopiedApplication, clearDraft, setDraftId } = declarationSlice.actions
export default declarationSlice.reducer